import React, { useEffect, useState } from 'react'
import { BREAKPOINT, ThemeContextValue, uiPrimitives, useThemedStyles } from './theme'

/**
 * 站内确认对话框 —— 替代浏览器原生 window.confirm。
 *
 * 原生框吃不到宣纸/暗墨主题，也拼不进站点的按钮语言；这里复用首页同一套
 * uiPrimitives（tb / tbPrimary / tbOutline）与主题 token，深浅主题自动跟随。
 * 版式与 NoticeModal 同一规范（backdrop / paper2 盒 / serif 文字）。
 *
 * 用法（Promise 风格，调用点保持顺序逻辑）：
 *   const ok = await appConfirm({title: '保存需要登录', message: '…', confirmText: '前往登录'})
 *   const name = await appPrompt({title: '曲谱名', defaultValue: '小星星', confirmText: '保存'})
 *
 * 实现由 EditorPage 渲染的 <AppDialogHost/> 提供；宿主不在时回落原生 confirm /
 * prompt（理论上不会走到 —— 保存只会发生在编辑器页）。
 */

export type AppDialogSpec = {
	title: string
	message?: string
	confirmText: string
	cancelText?: string
	/** 确认键带破坏性（如覆盖已有曲谱）→ 取消键用描边、确认键保持朱砂实心 */
	danger?: boolean
	/** 单键提示（无取消键）—— 用于「知道了」式提示，如跟弹受限引导 */
	singleAction?: boolean
}

/** 带一个输入框的对话框（目前只有「给曲谱起名」用到） */
export type AppPromptSpec = {
	title: string
	message?: string
	/** 输入框初始值 */
	defaultValue?: string
	placeholder?: string
	confirmText: string
	cancelText?: string
	/** 返回错误文案表示不能用这个名字；`null` 表示可以 */
	validate?: (value: string) => string | null
}

type ConfirmResolver = (spec: AppDialogSpec) => Promise<boolean>
type PromptResolver = (spec: AppPromptSpec) => Promise<string | null>

let confirmListener: ConfirmResolver | null = null
let promptListener: PromptResolver | null = null

export function appConfirm(spec: AppDialogSpec): Promise<boolean> {
	if (!confirmListener) return Promise.resolve(window.confirm(spec.message))
	return confirmListener(spec)
}

/** 单键提示（无取消键）—— 如「跟弹受限」引导。宿主不在时回落原生 alert。 */
export function appAlert(spec: Omit<AppDialogSpec, 'singleAction'>): Promise<void> {
	if (!confirmListener) {
		window.alert(spec.message)
		return Promise.resolve()
	}
	return confirmListener({...spec, singleAction: true}).then(() => {})
}

/**
 * 取一行文本（取消返回 `null`）。
 *
 * 与 `window.prompt` 的区别不只是主题：原生 prompt 在微信/部分内嵌浏览器里
 * **直接被禁用**（返回 null 且没有任何提示），用户会以为「点了保存没反应」。
 */
export function appPrompt(spec: AppPromptSpec): Promise<string | null> {
	if (!promptListener) {
		const v = window.prompt(spec.title, spec.defaultValue ?? '')
		return Promise.resolve(v)
	}
	return promptListener(spec)
}

export function AppDialogHost() {
	const classes = useThemedStyles(stylesFactory)
	const [spec, setSpec] = useState<AppDialogSpec | null>(null)
	const [resolver, setResolver] = useState<((v: boolean) => void) | null>(null)
	const [pspec, setPspec] = useState<AppPromptSpec | null>(null)
	const [pvalue, setPvalue] = useState('')
	const [presolver, setPresolver] = useState<((v: string | null) => void) | null>(null)

	useEffect(() => {
		confirmListener = (s) => new Promise<boolean>(resolve => {
			setSpec(s)
			setResolver(() => resolve)
		})
		promptListener = (s) => new Promise<string | null>(resolve => {
			setPspec(s)
			setPvalue(s.defaultValue ?? '')
			setPresolver(() => resolve)
		})
		return () => { confirmListener = null; promptListener = null }
	}, [])

	const promptError = pspec?.validate?.(pvalue) ?? null

	// Esc = 取消。按 document 监听：弹窗打开时焦点仍在 body，键盘事件到不了盒子。
	useEffect(() => {
		if (!spec && !pspec) return
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				if (spec) done(false)
				else donePrompt(null)
			}
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [spec, pspec])

	function done(v: boolean) {
		setSpec(null)
		resolver?.(v)
	}

	function donePrompt(v: string | null) {
		setPspec(null)
		presolver?.(v)
	}

	if (!spec && !pspec) return null

	if (pspec) {
		const submit = () => {
			if (promptError) return
			donePrompt(pvalue.trim())
		}
		return <div className={classes.backdrop}>
			<div className={classes.box} role="dialog" aria-modal="true" aria-label={pspec.title}>
				<h3 className={classes.title}>{pspec.title}</h3>
				{pspec.message && <p className={classes.body}>{pspec.message}</p>}
				<input
					className={classes.input}
					value={pvalue}
					autoFocus
					placeholder={pspec.placeholder}
					onChange={e => setPvalue(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') { e.preventDefault(); submit() }
					}}
				/>
				{promptError && <p className={classes.err}>{promptError}</p>}
				<div className={classes.acts}>
					<button className={classes.tb + ' ' + classes.tbOutline} onClick={() => donePrompt(null)}>
						{pspec.cancelText || '取消'}
					</button>
					<button className={classes.tb + ' ' + classes.tbPrimary} disabled={!!promptError} onClick={submit}>
						{pspec.confirmText}
					</button>
				</div>
			</div>
		</div>
	}

	return <div className={classes.backdrop}>
		<div className={classes.box} role="dialog" aria-modal="true" aria-label={spec!.title}>
			<h3 className={classes.title}>{spec!.title}</h3>
			{spec!.message && <p className={classes.body}>{spec!.message}</p>}
			<div className={classes.acts}>
				{/* 破坏性确认（覆盖已有曲谱）：取消用描边、确认用朱砂；普通确认：取消用普通、确认用朱砂。
				    单键提示（singleAction，如跟弹受限引导）不渲染取消键，确认键自动聚焦。 */}
				{!spec!.singleAction && <button className={classes.tb + (spec!.danger ? ' ' + classes.tbOutline : '')} autoFocus onClick={() => done(false)}>
					{spec!.cancelText || '取消'}
				</button>}
				<button className={classes.tb + ' ' + classes.tbPrimary} autoFocus={!!spec!.singleAction} onClick={() => done(true)}>
					{spec!.confirmText}
				</button>
			</div>
		</div>
	</div>
}

const stylesFactory = (t: ThemeContextValue) => ({
	...uiPrimitives(t),

	backdrop: {
		position: 'fixed' as const,
		// 不用 `inset: 0` 简写：UC/微信等内核（Chromium < 87）不认，
		// 忽略后 fixed 元素零尺寸，整个弹窗塌在原地。逐条写偏移。
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		background: 'rgba(0,0,0,.42)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 2000,
		padding: 16,
	},
	box: {
		background: t.palette.paper2,
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.lg,
		boxShadow: t.shadows.lg,
		padding: 22,
		width: 420,
		maxWidth: '100%',
		color: t.palette.ink,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			padding: 18,
		},
	},
	title: {
		fontFamily: t.fonts.serif,
		fontSize: 17,
		fontWeight: 700,
		margin: '0 0 10px',
	},
	body: {
		fontFamily: t.fonts.serif,
		fontSize: 14,
		lineHeight: 1.75,
		color: t.palette.ink2,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		margin: '0 0 18px',
	},
	/** 命名框：与编辑器源码区同一套纸色，避免白色输入框在宣纸上显得突兀 */
	input: {
		width: '100%',
		boxSizing: 'border-box' as const,
		padding: '9px 11px',
		margin: '0 0 14px',
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.sm,
		background: t.palette.paper3,
		color: t.palette.ink,
		fontFamily: t.fonts.serif,
		fontSize: 15,
		outline: 'none',
		'&:focus': {
			borderColor: t.palette.celadonInk,
		},
	},
	err: {
		margin: '4px 0 0',
		color: t.palette.cinnabar,
		fontSize: 12.5,
	},
	acts: {
		display: 'flex',
		gap: 8,
		justifyContent: 'flex-end',
	},
})
